import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Search,
  Star,
  Rocket,
  Target,
  ListTodo,
  CircleCheck,
  AlertTriangle,
  HelpCircle,
  X,
  Command,
  ArrowRight,
  Loader2,
  Book,
  FileText,
  ChevronDown,
  ChevronRight
} from 'lucide-react'
import { searchService, type SearchResults, type HelpEntry, type UserDataResult } from '../../services/searchService'

interface GlobalSearchProps {
  isOpen: boolean
  onClose: () => void
}

// Get icon for item type
const getTypeIcon = (type: string) => {
  switch (type) {
    case 'dream': return <Star className="h-4 w-4 text-warn" />
    case 'skill': return <Target className="h-4 w-4 text-info" />
    case 'habit': return <Target className="h-4 w-4 text-brand" />
    case 'aspiration': return <Rocket className="h-4 w-4 text-info" />
    case 'goal': return <Target className="h-4 w-4 text-brand" />
    case 'task': return <ListTodo className="h-4 w-4 text-ok" />
    case 'action_item': return <CircleCheck className="h-4 w-4 text-info" />
    case 'roadblock': return <AlertTriangle className="h-4 w-4 text-bad" />
    case 'help': return <HelpCircle className="h-4 w-4 text-brand" />
    default: return <FileText className="h-4 w-4 text-muted" />
  }
}

// Get navigation path for item type
const getItemPath = (type: string, id: number) => {
  switch (type) {
    case 'dream': return `/dreams/${id}`
    case 'skill': return `/skills/${id}`
    case 'habit': return `/habits/${id}`
    case 'goal': return `/goals/${id}`
    case 'task': return `/tasks/${id}`
    default: return '/'
  }
}

// Get type label
const getTypeLabel = (type: string) => {
  switch (type) {
    case 'dream': return 'Dreams'
    case 'skill': return 'Skills'
    case 'habit': return 'Habits'
    case 'aspiration': return 'Aspirations'
    case 'goal': return 'Goals'
    case 'task': return 'Tasks'
    case 'action_item': return 'Action Items'
    case 'roadblock': return 'Roadblocks'
    default: return type
  }
}

// Get category icon
const getCategoryIcon = (category: HelpEntry['category']) => {
  switch (category) {
    case 'getting-started': return <Book className="h-3 w-3" />
    case 'features': return <Star className="h-3 w-3" />
    case 'hierarchy': return <Target className="h-3 w-3" />
    case 'ai': return <Rocket className="h-3 w-3" />
    case 'scheduling': return <ListTodo className="h-3 w-3" />
    case 'settings': return <CircleCheck className="h-3 w-3" />
    default: return <FileText className="h-3 w-3" />
  }
}

export function GlobalSearch({ isOpen, onClose }: GlobalSearchProps) {
  const navigate = useNavigate()
  const inputRef = useRef<HTMLInputElement>(null)
  const listRef = useRef<HTMLDivElement>(null)
  
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<SearchResults | null>(null)
  const [loading, setLoading] = useState(false)
  const [searchError, setSearchError] = useState<string | null>(null)
  const [retryTick, setRetryTick] = useState(0)
  const [indexing, setIndexing] = useState(false)
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(new Set())
  
  // Toggle section collapse
  const toggleSection = (section: string) => {
    setCollapsedSections(prev => {
      const next = new Set(prev)
      if (next.has(section)) {
        next.delete(section)
      } else {
        next.add(section)
      }
      return next
    })
  }
  
  // Map plural key to singular type for navigation
  const keyToType: Record<string, string> = {
    'dreams': 'dream',
    'skills': 'skill',
    'habits': 'habit',
    'aspirations': 'aspiration',
    'goals': 'goal',
    'tasks': 'task',
    'action_items': 'action_item',
    'roadblocks': 'roadblock'
  }
  
  // Flatten results for keyboard navigation
  const flatResults = React.useMemo(() => {
    if (!results) return []
    
    const items: Array<{ type: 'user' | 'help', data: UserDataResult | HelpEntry, itemType?: string }> = []
    
    // Add user data results
    const userDataTypes = ['dreams', 'goals', 'skills', 'habits', 'tasks', 'action_items', 'roadblocks'] as const
    for (const key of userDataTypes) {
      const typeItems = results.userData[key] || []
      for (const item of typeItems) {
        items.push({
          type: 'user',
          data: item,
          itemType: keyToType[key]
        })
      }
    }
    
    // Add help results
    for (const help of results.helpDocs) {
      items.push({ type: 'help', data: help })
    }
    
    return items
  }, [results])
  
  // Focus input and preload index when opened
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 50)
      setQuery('')
      setResults(null)
      setSearchError(null)
      setSelectedIndex(0)
      setCollapsedSections(new Set()) // Reset collapsed sections
      
      // Preload the search index
      setIndexing(true)
      searchService.preloadIndex().finally(() => setIndexing(false))
    }
  }, [isOpen])
  
  /* Debounced search. 2026-08-29: the previous query's results stayed on
     screen while a new one ran, and a failed search left them there for good —
     an answer to a question nobody is asking any more. Results are dropped the
     moment the query changes, and a failure now says so instead of falling
     through to "no results". */
  useEffect(() => {
    setSearchError(null)
    setResults(null)
    setSelectedIndex(0)

    if (!query || query.length < 2) return

    let cancelled = false
    const timeoutId = setTimeout(async () => {
      setLoading(true)
      try {
        const searchResults = await searchService.search(query)
        if (cancelled) return
        setResults(searchResults)
        setSelectedIndex(0)
      } catch (error) {
        console.error('Search error:', error)
        if (!cancelled) setSearchError('We could not run that search just now.')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }, 300)

    return () => { cancelled = true; clearTimeout(timeoutId) }
  }, [query, retryTick])
  
  // Handle keyboard navigation
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose()
      return
    }
    
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setSelectedIndex(prev => Math.min(prev + 1, flatResults.length - 1))
      return
    }
    
    if (e.key === 'ArrowUp') {
      e.preventDefault()
      setSelectedIndex(prev => Math.max(prev - 1, 0))
      return
    }
    
    if (e.key === 'Enter' && flatResults[selectedIndex]) {
      e.preventDefault()
      const result = flatResults[selectedIndex]
      if (result.type === 'user') {
        const userItem = result.data as UserDataResult
        const itemType = result.itemType || 'goal'
        navigate(getItemPath(itemType, userItem.id))
      } else {
        const helpItem = result.data as HelpEntry
        if (helpItem.path) {
          navigate(helpItem.path)
        }
      }
      onClose()
    }
  }, [flatResults, selectedIndex, navigate, onClose])
  
  // Scroll selected item into view
  useEffect(() => {
    if (listRef.current) {
      const selectedEl = listRef.current.querySelector(`[data-index="${selectedIndex}"]`)
      if (selectedEl) {
        selectedEl.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
      }
    }
  }, [selectedIndex])
  
  // Handle item click
  const handleItemClick = (result: typeof flatResults[0]) => {
    if (result.type === 'user') {
      const userItem = result.data as UserDataResult
      const itemType = result.itemType || 'goal'
      navigate(getItemPath(itemType, userItem.id))
    } else {
      const helpItem = result.data as HelpEntry
      if (helpItem.path) {
        navigate(helpItem.path)
      }
    }
    onClose()
  }
  
  if (!isOpen) return null
  
  // Check if we have any user data results
  const hasUserData = results && (
    results.userData.dreams.length > 0 ||
    results.userData.aspirations.length > 0 ||
    results.userData.goals.length > 0 ||
    (results.userData.skills?.length || 0) > 0 ||
    (results.userData.habits?.length || 0) > 0 ||
    results.userData.tasks.length > 0 ||
    results.userData.action_items.length > 0 ||
    results.userData.roadblocks.length > 0
  )
  
  const hasHelpDocs = results && results.helpDocs.length > 0
  
  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Search Modal. 2026-08-29: it was an unannounced <div> — no dialog
          role, and an input with a placeholder standing in for a label. */}
      <div className="fixed inset-0 z-50 flex items-start justify-center pt-[8vh] sm:pt-[15vh] px-3 sm:px-0">
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="global-search-title"
          onKeyDown={handleKeyDown}
          className="w-full max-w-2xl bg-surface rounded-2xl shadow-2xl border border-soft overflow-hidden"
          onClick={e => e.stopPropagation()}
        >
          <h2 id="global-search-title" className="sr-only">Search</h2>
          {/* Search Input */}
          <div className="relative flex items-center border-b border-soft">
            <Search className="absolute left-4 h-5 w-5 text-muted" aria-hidden="true" />
            <label htmlFor="global-search-input" className="sr-only">
              Search your data and help
            </label>
            <input
              id="global-search-input"
              ref={inputRef}
              type="text"
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="Search your data and help..."
              className="w-full py-4 pl-12 pr-14 text-lg bg-transparent text-primary placeholder-muted focus:outline-none"
            />
            {loading ? (
              <Loader2 className="absolute right-4 h-5 w-5 text-muted animate-spin" />
            ) : (
              <button
                onClick={onClose}
                aria-label="Close search"
                className="absolute right-2 inline-flex items-center justify-center min-h-touch min-w-touch rounded hover:bg-surface-alt"
              >
                <X className="h-4 w-4 text-muted" />
              </button>
            )}
          </div>
          
          {/* Results */}
          <div ref={listRef} className="max-h-[60vh] overflow-y-auto">
            {/* Initial state - no query */}
            {!query && (
              <div className="p-6 text-center">
                {indexing ? (
                  <div className="flex items-center justify-center space-x-2 text-muted">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span className="text-sm">Loading your data...</span>
                  </div>
                ) : (
                  <>
                    <div className="flex items-center justify-center space-x-2 text-muted">
                      <Command className="h-4 w-4" />
                      <span className="text-sm">Type to search your data and help documentation</span>
                    </div>
                    <div className="mt-4 flex flex-wrap justify-center gap-2">
                      <span className="px-2 py-1 text-xs bg-surface-alt rounded text-muted">
                        Try "goals"
                      </span>
                      <span className="px-2 py-1 text-xs bg-surface-alt rounded text-muted">
                        Try "how to create"
                      </span>
                      <span className="px-2 py-1 text-xs bg-surface-alt rounded text-muted">
                        Try "schedule"
                      </span>
                    </div>
                  </>
                )}
              </div>
            )}
            
            {/* Loading state */}
            {query && loading && !results && (
              <div className="p-6 text-center text-muted">
                <Loader2 className="h-6 w-6 animate-spin mx-auto mb-2" />
                <span>Searching...</span>
              </div>
            )}
            
            {/* Search failed. This is NOT an empty state: "nothing matched" and
                "we could not look" are different answers (2026-08-29). */}
            {query && searchError && !loading && (
              <div className="p-6 text-center" role="alert">
                <AlertTriangle className="h-8 w-8 mx-auto mb-2 text-warn" aria-hidden="true" />
                <p className="text-primary">{searchError}</p>
                <p className="text-sm text-muted mt-1">
                  Your search never reached us, so there is nothing to show yet.
                </p>
                <button
                  onClick={() => setRetryTick(t => t + 1)}
                  className="mt-3 inline-flex items-center justify-center min-h-touch px-4 rounded-lg border border-soft text-sm font-medium text-primary hover:bg-surface-alt"
                >
                  Try again
                </button>
              </div>
            )}

            {/* No results */}
            {query && !searchError && results && results.totalCount === 0 && (
              <div className="p-6 text-center text-muted">
                <Search className="h-8 w-8 mx-auto mb-2 opacity-50" aria-hidden="true" />
                <p>Nothing here matches "{query}"</p>
                <p className="text-sm mt-1">Try another word for it, or fewer words</p>
              </div>
            )}
            
            {/* Results List */}
            {results && results.totalCount > 0 && (
              <div className="py-2">
                {/* User Data Section */}
                {hasUserData && (
                  <>
                    <button
                      onClick={() => toggleSection('userData')}
                      className="w-full px-4 py-2 flex items-center justify-between text-xs font-semibold text-secondary uppercase tracking-wider bg-surface-alt hover:bg-default transition-colors"
                    >
                      <span className="flex items-center gap-2">
                        {collapsedSections.has('userData') ? (
                          <ChevronRight className="h-3.5 w-3.5" />
                        ) : (
                          <ChevronDown className="h-3.5 w-3.5" />
                        )}
                        Your Data
                      </span>
                      <span className="text-muted font-normal normal-case">
                        {Object.values(results.userData).reduce((sum, arr) => sum + arr.length, 0)} items
                      </span>
                    </button>
                    {!collapsedSections.has('userData') && Object.entries(results.userData).map(([pluralType, items]) => {
                      if (items.length === 0) return null
                      const itemType = keyToType[pluralType] || pluralType
                      
                      return items.map((item) => {
                        const globalIdx = flatResults.findIndex(
                          r => r.type === 'user' && (r.data as UserDataResult).id === item.id && r.itemType === itemType
                        )
                        
                        return (
                          <button
                            key={`${pluralType}-${item.id}`}
                            data-index={globalIdx}
                            onClick={() => handleItemClick(flatResults[globalIdx])}
                            className={`w-full px-4 py-3 flex items-start gap-3 text-left transition-colors ${
                              globalIdx === selectedIndex 
                                ? 'bg-brand-subtle' 
                                : 'hover:bg-surface-alt'
                            }`}
                          >
                            <div className="mt-0.5">
                              {getTypeIcon(itemType)}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <span className="font-medium text-primary truncate">
                                  {item.title}
                                </span>
                                <span className="text-xs px-1.5 py-0.5 bg-surface-alt rounded text-muted flex-shrink-0">
                                  {getTypeLabel(itemType)}
                                </span>
                              </div>
                              {item.description && (
                                <p className="text-sm text-muted truncate mt-0.5">
                                  {item.description}
                                </p>
                              )}
                            </div>
                            <ArrowRight className={`h-4 w-4 mt-1 flex-shrink-0 transition-opacity ${
                              globalIdx === selectedIndex ? 'opacity-100 text-brand' : 'opacity-0'
                            }`} />
                          </button>
                        )
                      })
                    })}
                  </>
                )}
                
                {/* Help Documentation Section */}
                {hasHelpDocs && (
                  <>
                    <button
                      onClick={() => toggleSection('helpDocs')}
                      className="w-full px-4 py-2 flex items-center justify-between text-xs font-semibold text-secondary uppercase tracking-wider bg-surface-alt hover:bg-default transition-colors mt-2"
                    >
                      <span className="flex items-center gap-2">
                        {collapsedSections.has('helpDocs') ? (
                          <ChevronRight className="h-3.5 w-3.5" />
                        ) : (
                          <ChevronDown className="h-3.5 w-3.5" />
                        )}
                        Help & Documentation
                      </span>
                      <span className="text-muted font-normal normal-case">
                        {results.helpDocs.length} items
                      </span>
                    </button>
                    {!collapsedSections.has('helpDocs') && results.helpDocs.map((help) => {
                      const globalIdx = flatResults.findIndex(
                        r => r.type === 'help' && (r.data as HelpEntry).id === help.id
                      )
                      
                      return (
                        <button
                          key={help.id}
                          data-index={globalIdx}
                          onClick={() => handleItemClick(flatResults[globalIdx])}
                          className={`w-full px-4 py-3 flex items-start gap-3 text-left transition-colors ${
                            globalIdx === selectedIndex 
                              ? 'bg-brand-subtle' 
                              : 'hover:bg-surface-alt'
                          }`}
                        >
                          <div className="mt-0.5">
                            <HelpCircle className="h-4 w-4 text-brand" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-primary">
                                {help.title}
                              </span>
                              <span className="text-xs px-1.5 py-0.5 bg-brand/10 rounded text-brand flex-shrink-0 flex items-center gap-1">
                                {getCategoryIcon(help.category)}
                                {help.category.replace('-', ' ')}
                              </span>
                            </div>
                            <p className="text-sm text-muted mt-0.5">
                              {help.description}
                            </p>
                          </div>
                          <ArrowRight className={`h-4 w-4 mt-1 flex-shrink-0 transition-opacity ${
                            globalIdx === selectedIndex ? 'opacity-100 text-brand' : 'opacity-0'
                          }`} />
                        </button>
                      )
                    })}
                  </>
                )}
              </div>
            )}
          </div>
          
          {/* Footer */}
          <div className="flex items-center justify-between px-4 py-2 border-t border-soft bg-surface-alt text-xs text-muted">
            <div className="flex items-center gap-4">
              <span className="flex items-center gap-1">
                <kbd className="px-1.5 py-0.5 bg-surface border border-strong rounded">↑</kbd>
                <kbd className="px-1.5 py-0.5 bg-surface border border-strong rounded">↓</kbd>
                Navigate
              </span>
              <span className="flex items-center gap-1">
                <kbd className="px-1.5 py-0.5 bg-surface border border-strong rounded">↵</kbd>
                Open
              </span>
              <span className="flex items-center gap-1">
                <kbd className="px-1.5 py-0.5 bg-surface border border-strong rounded">esc</kbd>
                Close
              </span>
            </div>
            {results && results.totalCount > 0 && (
              <span>{results.totalCount} result{results.totalCount !== 1 ? 's' : ''}</span>
            )}
          </div>
        </div>
      </div>
    </>
  )
}

